import React, { useState, useEffect } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, CircularProgress, IconButton,
  TextField, InputAdornment, Alert, Fade,
  ToggleButtonGroup, ToggleButton, Stack,
  Accordion, AccordionSummary, AccordionDetails,
} from "@mui/material";
import CloseIcon               from "@mui/icons-material/Close";
import EditIcon                from "@mui/icons-material/Edit";
import SaveIcon                from "@mui/icons-material/Save";
import AddCircleOutlineIcon    from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ExpandMoreIcon          from "@mui/icons-material/ExpandMore";
import UnfoldMoreIcon          from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon          from "@mui/icons-material/UnfoldLess";
import { batchUpdateHoldings } from "../../../api/portfolio_api";
import { fmtNumber } from "./portfolioHelpers";
import { getCurrencySymbol, fmtL } from "../../../utils/currency"; // ← adjust path to match your project

const today = new Date().toISOString().split("T")[0];

const MODE_CFG = {
  buy: {
    label: "Buy",
    icon: <AddCircleOutlineIcon sx={{ fontSize: 16 }} />,
    color: "var(--color-success)",
    bgActive: "rgba(16,185,129,0.12)",
    borderActive: "var(--color-success)",
    inputLabel: "Shares to buy",
    inputPrefix: "+",
    priceLabel: "Price per share (buy)",
  },
  sell: {
    label: "Sell",
    icon: <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />,
    color: "var(--color-error)",
    bgActive: "rgba(239,68,68,0.12)",
    borderActive: "var(--color-error)",
    inputLabel: "Shares to sell",
    inputPrefix: "−",
    priceLabel: "Price per share (sell)",
  },
};

const inputSx = (accentColor, hasError = false) => ({
  fontSize: "var(--font-size-sm)",
  borderRadius: 1,
  bgcolor: "var(--color-bg-secondary)",
  color: "var(--color-text-primary)",
  "& fieldset": { borderColor: hasError ? "var(--color-error)" : "var(--color-border-primary)" },
  "&:hover fieldset": { borderColor: hasError ? "var(--color-error)" : "var(--color-border-secondary)" },
  "&.Mui-focused fieldset": {
    borderColor: hasError ? "var(--color-error)" : accentColor,
    borderWidth: "1px",
  },
});

const computeResult = (h) => {
  const qty = parseFloat(h.qty) || 0;
  const tradePrice = parseFloat(h.tradePrice) > 0 ? parseFloat(h.tradePrice) : h.marketPrice;
  if (h.mode === "buy") {
    return { shares: h.origShares + qty, cost: h.origCost + qty * tradePrice };
  }
  const avgCostPerShare = h.origShares > 0 ? h.origCost / h.origShares : 0;
  return { shares: h.origShares - qty, cost: Math.max(h.origCost - qty * avgCostPerShare, 0) };
};

const BUY_PRESETS = [1, 5, 10, 25, 50];

/* ════════════════════════════════
   HOLDING CARD BODY
   ════════════════════════════════ */
const HoldingCardBody = ({ h, idx, onChange, sym }) => {
  const cfg    = MODE_CFG[h.mode];
  const qty    = parseFloat(h.qty) || 0;
  const tradeP = parseFloat(h.tradePrice) || 0;
  const { shares: resultShares, cost: resultCost } = computeResult(h);

  const avgCostPerShare = h.origShares > 0 ? h.origCost / h.origShares : 0;
  const sellOver  = h.mode === "sell" && qty > h.origShares;
  const hasQty    = qty > 0;
  const isInvalid = sellOver || resultShares < 0;

  const realizedGL = h.mode === "sell" && qty > 0
    ? qty * (tradeP > 0 ? tradeP : h.marketPrice) - qty * avgCostPerShare
    : 0;

  const sellPresets = [1, 5, 10, Math.floor(h.origShares / 2), h.origShares]
    .filter((v, i, a) => v > 0 && v <= h.origShares && a.indexOf(v) === i)
    .slice(0, 5);
  const qtyPresets = h.mode === "sell" ? sellPresets : BUY_PRESETS;

  const handleModeChange = (_, newMode) => {
    if (!newMode) return;
    onChange(idx, "mode", newMode);
    onChange(idx, "qty", "");
    onChange(idx, "tradePrice", h.marketPrice.toFixed(2));
  };

  const handleField = (field) => (e) => {
    const v = e.target.value;
    if (v === "" || /^\d*\.?\d*$/.test(v)) onChange(idx, field, v);
  };

  return (
    <Stack spacing={2.5}>

      {/* Stock info panel */}
      <Box sx={{ bgcolor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)", borderRadius: 1, p: 1.75 }}>
        {[
          { label: "Market Price",     value: `${sym}${fmtNumber(h.marketPrice)}`,   color: "var(--color-success)" },
          { label: "Current Shares",   value: fmtL(h.origShares, 2) },
          { label: "Avg Cost / Share", value: `${sym}${fmtNumber(avgCostPerShare)}` },
          { label: "Total Cost Basis", value: `${sym}${fmtNumber(h.origCost)}` },
        ].map(({ label, value, color }, i) => (
          <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: i > 0 ? 0.75 : 0 }}>
            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.03em">
              {label}
            </Typography>
            <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color={color || "var(--color-text-primary)"}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Mode toggle */}
      <Box>
        <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-medium)" color="var(--color-text-primary)" mb={1}>
          Action
        </Typography>
        <ToggleButtonGroup
          value={h.mode} exclusive fullWidth onChange={handleModeChange}
          sx={{
            height: 38,
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontSize: "var(--font-size-sm)",
              fontWeight: "var(--font-weight-semibold)",
              border: "1px solid var(--color-border-primary)",
              color: "var(--color-text-secondary)",
              gap: 0.75, py: 0.5,
              "&:hover": { bgcolor: "var(--color-surface-hover)" },
            },
          }}
        >
          {["buy", "sell"].map((m) => (
            <ToggleButton key={m} value={m} sx={{
              "&.Mui-selected": {
                bgcolor: MODE_CFG[m].bgActive,
                color: MODE_CFG[m].color,
                borderColor: MODE_CFG[m].borderActive,
                "&:hover": { bgcolor: MODE_CFG[m].bgActive },
              },
            }}>
              {MODE_CFG[m].icon}&nbsp;{MODE_CFG[m].label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Quantity */}
      <Box>
        <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-medium)" color="var(--color-text-primary)" mb={0.75}>
          {cfg.inputLabel}
        </Typography>
        <TextField
          fullWidth size="small" type="text" placeholder="0"
          value={h.qty} error={sellOver}
          helperText={sellOver ? `Max: ${h.origShares}` : ""}
          onChange={handleField("qty")}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography fontSize="var(--font-size-base)" fontWeight="var(--font-weight-semibold)" color={cfg.color}>
                  {cfg.inputPrefix}
                </Typography>
              </InputAdornment>
            ),
            sx: inputSx(cfg.color, sellOver),
          }}
          FormHelperTextProps={{ sx: { mx: 0, mt: 0.5, fontSize: "var(--font-size-sm)", color: "var(--color-error)" } }}
        />
        <Box display="flex" gap={0.75} mt={1}>
          {qtyPresets.map((val) => (
            <Button key={val} size="small"
              onClick={() => onChange(idx, "qty", val.toString())}
              sx={{
                minWidth: 0, flex: 1,
                fontSize: "var(--font-size-sm)",
                fontWeight: "var(--font-weight-semibold)",
                textTransform: "none", py: 0.35, borderRadius: 0.75,
                border: "1px solid",
                color:       parseFloat(h.qty) === val ? cfg.color : "var(--color-text-secondary)",
                bgcolor:     parseFloat(h.qty) === val ? cfg.bgActive : "transparent",
                borderColor: parseFloat(h.qty) === val ? cfg.borderActive : "var(--color-border-primary)",
                "&:hover": { bgcolor: cfg.bgActive, borderColor: cfg.borderActive, color: cfg.color },
              }}
            >
              {h.mode === "sell" && val === h.origShares ? "All" : val}
            </Button>
          ))}
        </Box>
      </Box>

      {/* Price */}
      <Box>
        <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-medium)" color="var(--color-text-primary)" mb={0.75}>
          {cfg.priceLabel}
        </Typography>
        <TextField
          fullWidth size="small" type="text"
          value={h.tradePrice} onChange={handleField("tradePrice")}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">{sym}</Typography>
              </InputAdornment>
            ),
            sx: inputSx(cfg.color),
          }}
        />
        <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" mt={0.5}>
          {h.mode === "sell"
            ? `Avg cost/share is ${sym}${fmtNumber(avgCostPerShare)} — sell price used only for realized G/L`
            : `Defaults to market price ${sym}${fmtNumber(h.marketPrice)}. Override if your trade price differs.`}
        </Typography>
      </Box>

      {/* Date */}
      <Box>
        <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-medium)" color="var(--color-text-primary)" mb={0.75}>
          Transaction date
        </Typography>
        <TextField
          fullWidth size="small" type="date"
          value={h.purchaseDate}
          onChange={(e) => onChange(idx, "purchaseDate", e.target.value)}
          InputProps={{ sx: inputSx("var(--color-primary)") }}
          inputProps={{ max: today }}
        />
      </Box>

      {/* Cost basis breakdown */}
      {hasQty && (
        <Box sx={{ bgcolor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)", borderRadius: 1, p: 1.75 }}>
          <Typography
            fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)"
            color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.05em"
            mb={1.25} display="block"
          >
            Cost Basis Calculation
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.03em">
              Existing total cost
            </Typography>
            <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-primary)">
              {sym}{fmtNumber(h.origCost)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75 }}>
            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.03em">
              {h.mode === "buy"
                ? `Buy (${qty} × ${sym}${fmtNumber(tradeP > 0 ? tradeP : h.marketPrice)})`
                : `Remove (${qty} × ${sym}${fmtNumber(avgCostPerShare)} avg)`}
            </Typography>
            <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)"
              color={h.mode === "buy" ? "var(--color-success)" : "var(--color-error)"}>
              {h.mode === "buy"
                ? `+${sym}${fmtNumber(qty * (tradeP > 0 ? tradeP : h.marketPrice))}`
                : `−${sym}${fmtNumber(qty * avgCostPerShare)}`}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.25, pt: 1.25, borderTop: "1px solid var(--color-border-primary)" }}>
            <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-secondary)" textTransform="uppercase" letterSpacing="0.03em">
              New total cost basis
            </Typography>
            <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-bold)" color="var(--color-text-primary)">
              {sym}{fmtNumber(Math.max(resultCost, 0))}
            </Typography>
          </Box>
          {resultShares > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75 }}>
              <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.03em">
                New avg cost / share
              </Typography>
              <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-secondary)">
                {sym}{fmtNumber(resultCost / resultShares)}
              </Typography>
            </Box>
          )}
          {h.mode === "sell" && qty > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75, pt: 0.75, borderTop: "1px solid var(--color-border-primary)" }}>
              <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.03em">
                Realized gain / loss
              </Typography>
              <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)"
                color={realizedGL >= 0 ? "var(--color-success)" : "var(--color-error)"}>
                {realizedGL >= 0 ? "+" : "−"}{sym}{fmtNumber(Math.abs(realizedGL))}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Result summary */}
      <Fade in timeout={300}>
        <Box sx={{
          bgcolor: "var(--color-bg-secondary)", borderRadius: 1, p: 1.75,
          border: `1px solid ${hasQty && !isInvalid ? cfg.borderActive : "var(--color-border-secondary)"}`,
        }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" textTransform="uppercase" letterSpacing="0.03em">
              Resulting shares
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {hasQty && (
                <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                  {h.origShares} {h.mode === "buy" ? "+" : "−"} {qty} =
                </Typography>
              )}
              <Typography
                fontSize="var(--font-size-base)" fontWeight="var(--font-weight-semibold)"
                color={isInvalid ? "var(--color-error)" : "var(--color-text-primary)"}
              >
                {isInvalid ? "Invalid" : fmtNumber(resultShares, 2)}
              </Typography>
            </Box>
          </Box>
          {!isInvalid && resultShares >= 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.25, pt: 1.25, borderTop: "1px solid var(--color-border-secondary)" }}>
              <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)" textTransform="uppercase" letterSpacing="0.03em">
                New market value
              </Typography>
              <Typography fontSize="var(--font-size-base)" fontWeight="var(--font-weight-semibold)" color="var(--color-success)">
                {sym}{fmtNumber(resultShares * h.marketPrice)}
              </Typography>
            </Box>
          )}
        </Box>
      </Fade>

    </Stack>
  );
};

/* ════════════════════════════════
   MAIN DIALOG
   ════════════════════════════════ */
const BulkEditDialog = ({ open, onClose, portfolio, onSaved }) => {
  const [holdings, setHoldings] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  const sym = getCurrencySymbol(); // ← resolved once per render

  useEffect(() => {
    if (open && portfolio?.holdings) {
      setError("");
      const mapped = portfolio.holdings.map((h) => {
        const marketPrice = Number(h.stock_current_price || h.current_price || 0);
        return {
          ticker:       h.ticker,
          companyName:  h.company_name,
          marketPrice,
          origShares:   Number(h.shares || 0),
          origCost:     Number(h.cost_basis || 0),
          origDate:     h.purchase_date || today,
          mode:         "buy",
          qty:          "",
          tradePrice:   marketPrice.toFixed(2),
          purchaseDate: today,
        };
      });
      setHoldings(mapped);
      setExpanded(Object.fromEntries(mapped.map((h) => [h.ticker, true])));
    }
  }, [open, portfolio]);

  const handleChange = (idx, field, value) => {
    setHoldings((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const toggleOne   = (ticker) => setExpanded((prev) => ({ ...prev, [ticker]: !prev[ticker] }));
  const allExpanded = holdings.length > 0 && holdings.every((h) => expanded[h.ticker]);
  const anyExpanded = holdings.some((h) => expanded[h.ticker]);
  const expandAll   = () => setExpanded(Object.fromEntries(holdings.map((h) => [h.ticker, true])));
  const collapseAll = () => setExpanded(Object.fromEntries(holdings.map((h) => [h.ticker, false])));

  const changedRows = holdings.filter((h) => {
    const qty = parseFloat(h.qty) || 0;
    if (qty <= 0) return false;
    if (h.mode === "sell" && qty > h.origShares) return false;
    const { shares } = computeResult(h);
    return shares >= 0;
  });

  const hasChanges = changedRows.length > 0;

  const totalResultValue = holdings.reduce((sum, h) => {
    const qty = parseFloat(h.qty) || 0;
    if (qty > 0 && !(h.mode === "sell" && qty > h.origShares)) {
      const { shares } = computeResult(h);
      return sum + Math.max(shares, 0) * h.marketPrice;
    }
    return sum + h.origShares * h.marketPrice;
  }, 0);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const updates = changedRows.map((h) => {
        const { shares, cost } = computeResult(h);
        return { ticker: h.ticker, shares, cost_basis: Math.max(cost, 0), purchase_date: h.purchaseDate };
      });
      const result = await batchUpdateHoldings(portfolio.id, updates);
      if (result.authExpired) { setError("Session expired. Please log in again."); setSaving(false); return; }
      if (!result.success) throw new Error(result.message || "Failed to update holdings");
      onSaved?.();
      setSaving(false); 
      handleClose();
    } catch (err) {
      setError(err.message || "Failed to update holdings. Please try again.");
      setSaving(false);
    }
  };

  const handleClose = () => {
  setHoldings([]);
  setExpanded({});
  setError("");
  setSaving(false);   // ← add this
  onClose();
};

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
      PaperProps={{
        sx: {
          bgcolor: "var(--color-surface)", backgroundImage: "none",
          borderRadius: 1.5, border: "1px solid var(--color-border-primary)",
          boxShadow: "var(--shadow-xl)", m: { xs: 2, sm: 3 },
          maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 48px)" },
        },
      }}
    >
      {/* ── TITLE ── */}
      <DialogTitle sx={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        pb: 1.25, pt: 1.75, px: 2.5,
        borderBottom: "1px solid var(--color-border-primary)",
      }}>
        <Box display="flex" alignItems="center" gap={1.25}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 1,
            bgcolor: "var(--color-surface-hover)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <EditIcon sx={{ fontSize: 16, color: "var(--color-primary)" }} />
          </Box>
          <Box>
            <Typography variant="tableHeader" fontWeight="var(--font-weight-semibold)" color="var(--color-text-primary)">
              Bulk Edit Holdings
            </Typography>
            <Box display="flex" alignItems="center" gap={0.75} mt={0.25}>
              <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                {portfolio?.name}
              </Typography>
              {changedRows.length > 0 && (
                <Typography fontSize="var(--font-size-sm)" color="var(--color-primary)">
                  • {changedRows.length} pending
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" disabled={saving}
          sx={{ color: "var(--color-text-muted)", p: 0.5, "&:hover": { bgcolor: "var(--color-surface-hover)" } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      {/* ── CONTENT ── */}
      <DialogContent sx={{ px: 2.5, py: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{
            mb: 2, bgcolor: "var(--color-error-light)", color: "var(--color-text-primary)",
            border: "1px solid var(--color-error)", fontSize: "var(--font-size-sm)",
            "& .MuiAlert-icon": { color: "var(--color-error)" },
          }}>
            {error}
          </Alert>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" my={2}>
          <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
            {holdings.length} holding{holdings.length !== 1 ? "s" : ""} — leave quantity as 0 to skip.
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              size="small" onClick={expandAll} disabled={allExpanded}
              startIcon={<UnfoldMoreIcon sx={{ fontSize: 15 }} />}
              sx={{
                textTransform: "none",
                fontSize: "var(--font-size-sm)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--color-text-secondary)",
                border: "1px solid var(--color-border-primary)",
                py: 0.4, px: 1.25,
                "&:hover": { bgcolor: "var(--color-surface-hover)" },
                "&.Mui-disabled": { color: "var(--color-text-muted)", borderColor: "var(--color-border-primary)" },
              }}
            >
              Expand all
            </Button>
            <Button
              size="small" onClick={collapseAll} disabled={!anyExpanded}
              startIcon={<UnfoldLessIcon sx={{ fontSize: 15 }} />}
              sx={{
                textTransform: "none",
                fontSize: "var(--font-size-sm)",
                fontWeight: "var(--font-weight-semibold)",
                color: "var(--color-text-secondary)",
                border: "1px solid var(--color-border-primary)",
                py: 0.4, px: 1.25,
                "&:hover": { bgcolor: "var(--color-surface-hover)" },
                "&.Mui-disabled": { color: "var(--color-text-muted)", borderColor: "var(--color-border-primary)" },
              }}
            >
              Collapse all
            </Button>
          </Box>
        </Box>

        {/* ── Accordion list ── */}
        <Stack spacing={1}>
          {holdings.map((h, idx) => {
            const qty       = parseFloat(h.qty) || 0;
            const cfg       = MODE_CFG[h.mode];
            const { shares: resultShares } = computeResult(h);
            const sellOver  = h.mode === "sell" && qty > h.origShares;
            const isInvalid = sellOver || resultShares < 0;
            const isChanged = qty > 0 && !isInvalid;
            const isOpen    = !!expanded[h.ticker];

            return (
              <Accordion
                key={h.ticker}
                expanded={isOpen}
                onChange={() => toggleOne(h.ticker)}
                disableGutters
                elevation={0}
                sx={{
                  border: `1px solid ${isChanged ? cfg.borderActive : "var(--color-border-primary)"}`,
                  borderRadius: "var(--radius-md) !important",
                  overflow: "hidden",
                  bgcolor: isChanged
                    ? h.mode === "buy" ? "rgba(16,185,129,0.03)" : "rgba(239,68,68,0.03)"
                    : "var(--color-surface)",
                  transition: "border-color var(--transition-fast)",
                  "&:before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: "var(--color-text-muted)" }} />}
                  sx={{
                    px: 2.25, minHeight: 52,
                    bgcolor: isChanged ? cfg.bgActive : "var(--color-surface-hover)",
                    borderBottom: isOpen ? "1px solid var(--color-border-primary)" : "none",
                    "&:hover": { bgcolor: isChanged ? cfg.bgActive : "var(--color-surface-active)" },
                    "& .MuiAccordionSummary-content": { my: 0, alignItems: "center" },
                  }}
                >
                  <Box flex={1} display="flex" alignItems="center" gap={1.5}>
                    <Typography variant="chipText" fontWeight="var(--font-weight-bold)" color="var(--color-primary)">
                      {h.ticker}
                    </Typography>
                    <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" noWrap sx={{ maxWidth: 180 }}>
                      {h.companyName}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mr={1}>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                        {fmtL(h.origShares, 2)} shares
                      </Typography>
                      <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">
                        {sym}{fmtNumber(h.marketPrice)} / share
                      </Typography>
                    </Box>
                    {isChanged && (
                      <Box sx={{
                        px: 1, py: 0.25, borderRadius: "var(--radius-sm)",
                        bgcolor: cfg.bgActive, border: `1px solid ${cfg.borderActive}`,
                        whiteSpace: "nowrap",
                      }}>
                        <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-bold)" color={cfg.color}>
                          {h.mode === "buy" ? `+${qty}` : `−${qty}`} shares
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ px: 2.25, py: 2.5 }}>
                  <HoldingCardBody h={h} idx={idx} onChange={handleChange} sym={sym} />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>

        {/* ── Summary footer ── */}
        {hasChanges && (
          <Fade in timeout={300}>
            <Box sx={{
              mt: 2, bgcolor: "var(--color-surface-hover)",
              borderRadius: 1, p: 2, border: "1px solid var(--color-border-primary)",
            }}>
              {[
                { label: "Holdings to Update",   value: changedRows.length,                        color: "var(--color-primary)" },
                { label: "Total Holdings",        value: holdings.length,                           color: "var(--color-text-primary)" },
                { label: "Projected Total Value", value: `${sym}${fmtNumber(totalResultValue)}`,   color: "var(--color-success)" },
              ].map(({ label, value, color }, i) => (
                <Box key={i} display="flex" justifyContent="space-between" alignItems="center" mt={i > 0 ? 1 : 0}>
                  <Typography fontSize="var(--font-size-sm)" color="var(--color-text-secondary)">{label}</Typography>
                  <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color={color}>{value}</Typography>
                </Box>
              ))}
            </Box>
          </Fade>
        )}
      </DialogContent>

      {/* ── ACTIONS ── */}
      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1.5, gap: 1, borderTop: "1px solid var(--color-border-primary)" }}>
        <Button onClick={handleClose} disabled={saving} fullWidth
          sx={{
            textTransform: "none", fontSize: "var(--font-size-sm)",
            fontWeight: "var(--font-weight-medium)", color: "var(--color-text-secondary)",
            "&:hover": { bgcolor: "var(--color-surface-hover)" },
          }}>
          Cancel
        </Button>
        <Button
          variant="contained" onClick={handleSave}
          disabled={!hasChanges || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 15 }} />}
          fullWidth
          sx={{
            textTransform: "none", fontSize: "var(--font-size-sm)",
            fontWeight: "var(--font-weight-semibold)",
            bgcolor: "var(--color-primary)", boxShadow: "none",
            "&:hover": { bgcolor: "var(--color-primary-dark)", boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-muted)" },
          }}
        >
          {saving
            ? `Saving ${changedRows.length} changes…`
            : `Save ${changedRows.length} holding${changedRows.length !== 1 ? "s" : ""}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkEditDialog;